import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase/client';
import { getPasswordError } from '../utils/passwordValidation';
import SimpleAccountDashboard from '../components/Account/SimpleAccountDashboard';
import LogoNav from '../components/Navigation/LogoNav';
import { config } from '../utils/config';
import { useMobileDetection } from '../hooks/useMobileDetection';
import BottomSheet from '../components/BottomSheet/BottomSheet';
import DeleteAccountContent from '../components/Account/DeleteAccountContent';

import './AccountPage.css';

const AccountPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, session, signOut, changePassword } = useAuth();
  const { isMobile } = useMobileDetection();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);
  const [deleteStep, setDeleteStep] = useState('');
  
  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState<string | null>(null);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState<string | null>(null);

  // Handle password change
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (changePasswordLoading) return;
    
    // Reset states
    setChangePasswordError(null);
    setChangePasswordSuccess(null);
    
    // Validation
    if (!newPassword || !confirmNewPassword) {
      setChangePasswordError('Please fill in both new password fields');
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      setChangePasswordError('New passwords do not match');
      return;
    }
    
    const passwordError = getPasswordError(newPassword);
    if (passwordError) {
      setChangePasswordError(passwordError);
      return;
    }
    

    
    setChangePasswordLoading(true);
    
    try {
      // For already authenticated users, Supabase allows password updates without current password verification
      // The fact that they're signed in is sufficient authentication
      // Change password directly - Supabase handles authentication verification internally
      const result = await changePassword(newPassword);
      
      if (result.success) {
        setChangePasswordSuccess(result.message || 'Password changed successfully!');
        // Reset form
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        setShowChangePassword(false);
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setChangePasswordSuccess(null);
        }, 5000);
      } else {
        setChangePasswordError(result.message || 'Failed to change password');
      }
    } catch (error: any) {
      setChangePasswordError('An unexpected error occurred. Please try again.');
    } finally {
      setChangePasswordLoading(false);
    }
  };

  // Handle account deletion with progress tracking
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE MY ACCOUNT') {
      setDeleteError('Please type "DELETE MY ACCOUNT" exactly to confirm');
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);
    setDeleteProgress(0);
    
    const steps = [
      { message: 'Verifying authentication...', progress: 10 },
      { message: 'Canceling subscriptions...', progress: 25 },
      { message: 'Removing account data...', progress: 50 },
      { message: 'Deleting saved files...', progress: 75 },
      { message: 'Finalizing deletion...', progress: 90 },
      { message: 'Account deleted successfully', progress: 100 }
    ];

    // Show user that deletion is starting
    console.log('🔍 DEBUG: Account deletion initiated by user');

    try {
      console.log('🔍 DEBUG: Starting account deletion...');
      console.log('🔍 DEBUG: Session exists:', !!session);
      console.log('🔍 DEBUG: Access token exists:', !!session?.access_token);
      console.log('🔍 DEBUG: User ID:', user?.id);
      
      // Step 1: Verify authentication
      setDeleteStep(steps[0].message);
      setDeleteProgress(steps[0].progress);
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Get fresh session to ensure token is valid
      const { data: { session: freshSession }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !freshSession) {
        throw new Error('Unable to verify authentication. Please sign in again.');
      }
      
      console.log('🔍 DEBUG: Fresh session obtained:', !!freshSession);
      console.log('🔍 DEBUG: Fresh token exists:', !!freshSession.access_token);

      // Step 2: Start deletion process  
      setDeleteStep(steps[1].message);
      setDeleteProgress(steps[1].progress);
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 3: Call backend deletion
      setDeleteStep(steps[2].message);
      setDeleteProgress(steps[2].progress);
      
      // Call backend to delete user data
      const response = await fetch(`${config.API_URL}/api/delete-account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${freshSession.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🔍 DEBUG: Delete response status:', response.status);
      console.log('🔍 DEBUG: Delete response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('🔍 DEBUG: Delete error response:', errorText);
        throw new Error(`Failed to delete account: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      console.log('🔍 DEBUG: Account deleted successfully:', data);

      // Step 4: Storage cleanup
      setDeleteStep(steps[3].message);
      setDeleteProgress(steps[3].progress);
      await new Promise(resolve => setTimeout(resolve, 800));

      // Step 5: Finalizing
      setDeleteStep(steps[4].message);
      setDeleteProgress(steps[4].progress);
      await new Promise(resolve => setTimeout(resolve, 600));

      // Step 6: Complete
      setDeleteStep(steps[5].message);
      setDeleteProgress(steps[5].progress);
      setDeleteSuccess(true);
      
      // Perform cleanup and redirect in background (non-blocking)
      setTimeout(async () => {
        try {
          console.log('🔍 DEBUG: Starting background session cleanup...');
          
          // Clear local storage
          localStorage.clear();
          sessionStorage.clear();
          
          // Sign out from Supabase (best effort)
          await supabase.auth.signOut({ scope: 'global' }).catch(err => 
            console.log('Session signout failed (expected):', err.message)
          );
          
          // Clear auth context (best effort)
          await signOut().catch(err => 
            console.log('Auth context signout failed (expected):', err.message)
          );
          
          console.log('✅ Background cleanup completed');
                 } catch (cleanupError: any) {
           console.log('Cleanup error (non-critical):', cleanupError.message);
         }
        
                 // Force redirect regardless of cleanup success
         window.location.replace('/');
       }, 2000); // 2 second delay to show success message
       
       // Return early to prevent finally block from running
       return;
      
    } catch (error: any) {
      console.error('Error deleting account:', error);
      setDeleteError(error.message || 'Failed to delete account. Please try again.');
      setDeleteProgress(0);
      setDeleteStep('');
    } finally {
      if (!deleteSuccess) {
        setIsDeleting(false);
      }
    }
  };

  if (!user) {
    return (
      <div className="account-page">
        <LogoNav />
        <div className="account-container">
          <div className="account-header">
            <h1>Account Management</h1>
          </div>
          <div className="auth-required">
            <p>Please sign in to access your account management.</p>
            <button onClick={() => navigate('/')} className="btn-primary">
              Go to ZoningPal
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="account-page">
      <LogoNav />
      <div className="account-container">
        <div className="account-header">
          <h1>Account Management</h1>
        </div>
        
        <div className="account-content">
          {/* Account Dashboard Component */}
          <div className="dashboard-wrapper">
            <SimpleAccountDashboard />
          </div>

          {/* Change Password Section */}
          <div className="account-settings-section">
            <h2>Security Settings</h2>
            
            {/* Change Password Success Message */}
            {changePasswordSuccess && (
              <div className="change-password-success">
                <div className="success-icon">✓</div>
                <div className="success-text">{changePasswordSuccess}</div>
              </div>
            )}
            
            <div className="change-password-section">
              <div className="setting-item">
                <div className="setting-description">
                  <h3>Change Password</h3>
                  <p>Update your account password for better security.</p>
                </div>
                <button 
                  className="btn-secondary"
                  onClick={() => setShowChangePassword(!showChangePassword)}
                  disabled={changePasswordLoading}
                >
                  {showChangePassword ? 'Cancel' : 'Change Password'}
                </button>
              </div>
              
              {/* Change Password Form */}
              {showChangePassword && (
                <div className="change-password-form">
                  {/* Error Message */}
                  {changePasswordError && (
                    <div className="change-password-error">
                      <div className="error-icon">!</div>
                      <div className="error-text">{changePasswordError}</div>
                    </div>
                  )}
                  
                  <form onSubmit={handleChangePassword}>
                    <div className="form-group">
                      <label htmlFor="currentPassword">Current Password (Optional)</label>
                      <input
                        id="currentPassword"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Enter your current password (optional for security)"
                        className="auth-input"
                        disabled={changePasswordLoading}
                        autoComplete="current-password"
                      />
                      <small style={{ color: '#6b7280', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                        Current password is not required since you're already signed in
                      </small>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="newPassword">New Password</label>
                      <input
                        id="newPassword"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        placeholder="Enter your new password"
                        className="auth-input"
                        disabled={changePasswordLoading}
                        autoComplete="new-password"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="confirmNewPassword">Confirm New Password</label>
                      <input
                        id="confirmNewPassword"
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        required
                        placeholder="Confirm your new password"
                        className="auth-input"
                        disabled={changePasswordLoading}
                        autoComplete="new-password"
                      />
                      
                      {/* Password match indicator */}
                      {confirmNewPassword && newPassword && (
                        <div className={`password-match-indicator ${newPassword === confirmNewPassword ? 'match' : 'no-match'}`}>
                          {newPassword === confirmNewPassword ? (
                            <div className="match-success">
                              ✓ Passwords match
                            </div>
                          ) : (
                            <div className="match-error">
                              ✗ Passwords do not match
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="form-actions">
                      <button 
                        type="button"
                        className="btn-cancel"
                        onClick={() => {
                          setShowChangePassword(false);
                          setCurrentPassword('');
                          setNewPassword('');
                          setConfirmNewPassword('');
                          setChangePasswordError(null);
                        }}
                        disabled={changePasswordLoading}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit"
                        className={`btn-primary ${changePasswordLoading ? 'loading' : ''}`}
                        disabled={changePasswordLoading || !newPassword || !confirmNewPassword || newPassword !== confirmNewPassword}
                      >
                        {changePasswordLoading ? 'Changing Password...' : 'Change Password'}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>

          {/* Account Deletion Section */}
          <div className="danger-zone">
            <h2>Danger Zone</h2>
            <div className="danger-content">
              <div className="danger-description">
                <h3>Delete Account</h3>
                <p>
                  Permanently delete your ZoningPal account and all associated data. 
                  This action cannot be undone.
                </p>
                <ul>
                  <li>All saved reports will be deleted</li>
                  <li>Your subscription will be cancelled</li>
                  <li>All account data will be permanently removed</li>
                </ul>
              </div>
              <button 
                className="btn-danger"
                onClick={() => setShowDeleteConfirmation(true)}
                disabled={isDeleting}
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation - Mobile Bottom Sheet or Desktop Modal */}
      {showDeleteConfirmation && isMobile ? (
        <BottomSheet
          isOpen={showDeleteConfirmation}
          onClose={() => {
            setShowDeleteConfirmation(false);
            setDeleteConfirmText('');
            setDeleteError(null);
            setDeleteSuccess(false);
            setDeleteProgress(0);
            setDeleteStep('');
          }}
          title="Delete Account"
          className="delete-account"
        >
          <DeleteAccountContent
            deleteSuccess={deleteSuccess}
            isDeleting={isDeleting}
            deleteError={deleteError}
            deleteConfirmText={deleteConfirmText}
            deleteProgress={deleteProgress}
            deleteStep={deleteStep}
            onDeleteConfirmTextChange={setDeleteConfirmText}
            onDeleteAccount={handleDeleteAccount}
            onCancel={() => {
              setShowDeleteConfirmation(false);
              setDeleteConfirmText('');
              setDeleteError(null);
              setDeleteSuccess(false);
              setDeleteProgress(0);
              setDeleteStep('');
            }}
            isMobile={true}
          />
        </BottomSheet>
      ) : showDeleteConfirmation ? (
        <div className="delete-modal-overlay">
          <div className="delete-modal">
            <div className="delete-modal-header">
              <h2>Delete Account</h2>
              <button 
                className="delete-modal-close"
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  setDeleteConfirmText('');
                  setDeleteError(null);
                  setDeleteSuccess(false);
                  setDeleteProgress(0);
                  setDeleteStep('');
                }}
              >
                ×
              </button>
            </div>
            
            <div className="delete-modal-body">
              <DeleteAccountContent
                deleteSuccess={deleteSuccess}
                isDeleting={isDeleting}
                deleteError={deleteError}
                deleteConfirmText={deleteConfirmText}
                deleteProgress={deleteProgress}
                deleteStep={deleteStep}
                onDeleteConfirmTextChange={setDeleteConfirmText}
                onDeleteAccount={handleDeleteAccount}
                onCancel={() => {
                  setShowDeleteConfirmation(false);
                  setDeleteConfirmText('');
                  setDeleteError(null);
                  setDeleteSuccess(false);
                  setDeleteProgress(0);
                  setDeleteStep('');
                }}
                isMobile={false}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AccountPage; 